import { createContext, ReactNode, useCallback, useContext, useState } from 'react';

// ─── Theme ───────────────────────────────────────────────────────────────
export function applyInitialTheme() {
  if (typeof window === 'undefined') return;
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  if (prefersDark) document.documentElement.classList.add('dark');
}

export function toggleTheme() {
  document.documentElement.classList.toggle('dark');
}

// ─── Toast ───────────────────────────────────────────────────────────────
type Toast = { id: number; msg: string; kind: 'info' | 'success' | 'warn' | 'error' };

type ToastCtx = { push: (msg: string, kind?: Toast['kind']) => void };
const ToastContext = createContext<ToastCtx | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('ToastProvider missing');
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((msg: string, kind: Toast['kind'] = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, msg, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3000);
  }, []);
  return (
    <ToastContext.Provider value={{ push }}>
      {children}
      <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto max-w-sm rounded-md px-4 py-2 text-sm shadow-lg border ${
              t.kind === 'success'
                ? 'bg-green-50 border-green-300 text-green-800 dark:bg-green-900/30 dark:border-green-700 dark:text-green-200'
                : t.kind === 'warn'
                ? 'bg-yellow-50 border-yellow-300 text-yellow-800 dark:bg-yellow-900/30 dark:border-yellow-700 dark:text-yellow-200'
                : t.kind === 'error'
                ? 'bg-red-50 border-red-300 text-red-800 dark:bg-red-900/30 dark:border-red-700 dark:text-red-200'
                : 'bg-card border-border text-foreground'
            }`}
          >
            {t.msg}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// ─── Confirm Modal ───────────────────────────────────────────────────────
type ConfirmOpts = {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
};

type ConfirmCtx = { confirm: (opts: ConfirmOpts) => Promise<boolean> };
const ConfirmContext = createContext<ConfirmCtx | null>(null);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('ConfirmProvider missing');
  return ctx.confirm;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ opts: ConfirmOpts; resolve: (v: boolean) => void } | null>(null);
  const confirm = useCallback((opts: ConfirmOpts) => {
    return new Promise<boolean>((resolve) => setState({ opts, resolve }));
  }, []);
  const close = (result: boolean) => {
    state?.resolve(result);
    setState(null);
  };
  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {state && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50" onClick={() => close(false)}>
          <div className="bg-card border border-border rounded-lg shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-border">
              <h3 className="text-lg font-bold">{state.opts.title}</h3>
            </div>
            <div className="px-5 py-4 text-sm">{state.opts.message}</div>
            <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
              <button
                className="h-9 px-4 rounded-md bg-secondary text-secondary-foreground text-sm font-medium"
                onClick={() => close(false)}
              >
                {state.opts.cancelLabel ?? 'Annuler'}
              </button>
              <button
                className={`h-9 px-4 rounded-md text-sm font-medium ${
                  state.opts.destructive
                    ? 'bg-destructive text-destructive-foreground'
                    : 'bg-primary text-primary-foreground'
                }`}
                onClick={() => close(true)}
              >
                {state.opts.confirmLabel ?? 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}
